import React, { ErrorInfo } from "react";

/**
 * Error boundary component to catch and display errors in the application
 */
export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error; errorInfo?: ErrorInfo }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center flex-col p-4 bg-gradient-to-r from-background to-background/90">
          <div className="animate-pulse mb-8">
            <span
              role="img"
              aria-label="robot"
              className="text-6xl filter drop-shadow-lg"
            >
              🤖
            </span>
          </div>
          <h1 className="text-3xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-primary/80">
            Oops! Something went wrong
          </h1>
          <div className="bg-card rounded-xl p-6 max-w-2xl w-full mb-6 shadow-xl border border-border backdrop-blur-sm">
            <h2 className="font-semibold mb-3 flex items-center text-foreground/90">
              <span
                role="img"
                aria-label="magnifying glass"
                className="text-2xl mr-2"
              >
                🔍
              </span>
              Error Details
            </h2>
            <pre className="whitespace-pre-wrap text-sm bg-muted/50 p-4 rounded-lg font-mono text-muted-foreground overflow-auto max-h-[400px]">
              {this.state.error?.message || "An unexpected error occurred"}
              {this.state.errorInfo?.componentStack && (
                <>
                  {"\n\nComponent Stack:\n"}
                  {this.state.errorInfo.componentStack}
                </>
              )}
            </pre>
          </div>
          <button
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium 
              hover:opacity-90 active:scale-95 transition-all duration-200 
              flex items-center gap-2 shadow-lg"
            onClick={() => this.setState({ hasError: false })}
          >
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
